/**
 * Axios REST Methods
 *
 * This module provides standardized helper functions for making API requests.
 * It abstracts the Axios implementation details and provides a consistent interface
 * for all API calls throughout the application.
 *
 * Benefits:
 * - Consistent URL formatting (always prefixing with /)
 * - Standardized error handling
 * - DRY approach to API calls
 * - Easy to extend with additional functionality
 *
 * @module services/axios-rest-methods
 */

import axios from "axios";
import api from "../apiService/api";

/**
 * Perform a GET request to fetch data
 *
 * @param {string} url - The API endpoint path (without leading slash)
 * @param {Object} options - Axios request options (params, headers, etc.)
 * @returns {Promise} Promise resolving to the API response
 */
export const axiosFetch = (url, options = {}) => {
  return api.get(`/${url}`, options);
};

/**
 * Perform a POST request to create data
 *
 * @param {string} url - The API endpoint path (without leading slash)
 * @param {Object} body - Request body data
 * @param {Object} options - Axios request options (params, headers, etc.)
 * @returns {Promise} Promise resolving to the API response
 */
export const axiosPost = (url, body, options = {}) => {
  return api.post(`/${url}`, body, options);
};

/**
 * Perform a PATCH request to update data
 *
 * @param {string} url - The API endpoint path (without leading slash)
 * @param {Object} body - Request body data with fields to update
 * @returns {Promise} Promise resolving to the API response
 */
export const axiosPatch = (url, body) => {
  return api.patch(`/${url}`, body);
};

/**
 * Perform a DELETE request to remove data
 *
 * @param {string} url - The API endpoint path (without leading slash)
 * @param {Object} body - Optional request body data
 * @returns {Promise} Promise resolving to the API response
 */
export const axiosDelete = (url, body, queryParams = {}) => {
  return api.delete(`/${url}`, { data: body, params: queryParams });
};

/**
 * Upload a file with progress tracking
 *
 * @param {string} url - The API endpoint path or absolute URL
 * @param {Object} postData - File and other data to upload
 * @param {Function} uploadProgressCb - Callback function for upload progress
 * @param {boolean} isExternal - If true, use absolute URL without API base
 * @returns {Promise} Promise resolving to the API response
 */
export const axioxFileUpload = (
  url,
  postData,
  uploadProgressCb,
  isExternal = true
) => {
  // Create FormData for file upload
  const formData = new FormData();
  for (let prop in postData) {
    formData.append(prop, postData[prop]);
  }

  // Use direct axios for external URLs, or API instance for internal
  if (isExternal) {
    return axios.post(url, formData, {
      onUploadProgress: _uploadProgressHandler(uploadProgressCb)
    });
  } else {
    return api.post(`/${url}`, postData, {
      onUploadProgress: _uploadProgressHandler(uploadProgressCb)
    });
  }
};

export const axioxFileUpdate = (
  url,
  postData,
  params = {},
  uploadProgressCb
) => {
  return axios.put(url, postData, params, {
    onUploadProgress: _uploadProgressHandler(uploadProgressCb)
  });
};

/**
 * Get the application build file (index.html)
 * Used for version checking
 *
 * @returns {Promise} Promise resolving to the build file content
 */
export const axiosGetBuildFile = () => {
  return axios.get("/index.html", { responseType: "text" });
};

/**
 * Download a file as a blob
 *
 * @param {Object} options - Download options
 * @param {string} options.url - The API endpoint or absolute URL
 * @param {boolean} options.hasAbsoluteUrl - If true, use absolute URL without API base
 * @param {Object} options.queryParams - Query parameters for the request
 * @returns {Promise} Promise resolving to a blob
 */
export const axiosDownload = ({
  url,
  hasAbsoluteUrl = false,
  queryParams = {}
}) => {
  const respTypeBlob = { responseType: "blob" };
  return hasAbsoluteUrl
    ? axios.get(url, respTypeBlob)
    : api.get(`/${url}`, { ...respTypeBlob, params: queryParams });
};

/**
 * Helper function to calculate and report upload progress
 *
 * @param {Function} progressCb - Callback function to report progress percentage
 * @returns {Function} Handler function for Axios onUploadProgress
 * @private
 */
function _uploadProgressHandler(progressCb) {
  return (progressEvent) => {
    // Calculate percent completed based on loaded vs total bytes
    const percentCompleted = (progressEvent.loaded / progressEvent.total) * 100;
    // Report progress to the callback
    progressCb(percentCompleted);
  };
}
